import React, {useEffect, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../app/store";
import {
    addDraft,
    addGameLanguage,
    changeTab, duplicateGame,
    getAdminGameDetail,
    publishVersionPromise
} from "../../app/slices/adminGameView";
import {ADMIN_LANG, AVAILABLE_LANGUAGES, FEATURE_PDF_UPLOAD, STATUS_PUBLISHED} from "../../settings";
import {Button, Col, Container, Dropdown, Row, Tab, Table, Tabs} from "react-bootstrap";
import {formatDuration} from "./utils";
import {DataParams, GameLanguages} from "../../types/admin";
import {openSectionModal} from "../../app/slices/modals/sectionEditModal";
import AdminLoader from "./AdminLoader";
import {openModal} from "../../app/slices/modals/gameEditModal";
import AdminGameSessions from "./gameView/AdminGameSessions";
import {
    BarChart, BoxArrowUpRight,
    CheckCircleFill, ChevronDown, Eye,
    FileEarmark,
    FileEarmarkCheck,
    FileEarmarkText, FileEarmarkX, Gear, Grid, PlusCircleFill,
    Stopwatch,
    Trophy, XCircleFill,
} from "react-bootstrap-icons";
import SectionDetails from "./gameView/SectionDetails";
import QuestionEditModal from "./modals/QuestionEditModal";
import SessionTable from "./SessionTable";
import SectionEditModal from "./modals/SectionEditModal";
import SectionOrderModal from "./modals/SectionOrderModal";
import VersionPublishModal from "./gameView/VersionPublishModal";
import Confirm from "./Confirm";
import ErrorPage from "./ErrorPage";
import {openPreview} from "../../app/slices/offCanvas/previewOffCanvas";
import SectionPdfModal from "./modals/SectionPdfModal";
import GameCard from "./gameView/GameCard";


export const getVersionDetails = (is_published: boolean, is_draft: boolean, is_archived: boolean) => {
    if (is_published) {
        return {
            icon: <FileEarmarkCheck className='me-2'/>,
            label: ADMIN_LANG.status_published,
            className: 'version-published'
        };
    } else if (is_draft) {
        return {
            icon: <FileEarmarkText className='me-2'/>,
            label: ADMIN_LANG.status_draft,
            className: 'version-draft'
        };
    } else if (is_archived) {
        return {
            icon: <FileEarmarkX className='me-2'/>,
            label: ADMIN_LANG.status_archived,
            className: 'version-archived'
        };
    }
    return {
        icon: <FileEarmark className='me-2'/>,
        label: '',
        className: 'version-default'
    };
};

export const getVersionIcon = (is_published: boolean, is_draft: boolean, is_archived: boolean) => {
    const {icon, label} = getVersionDetails(is_published, is_draft, is_archived);

    return (
        <>
            {icon}
            {label && <span className='small'>({label})</span>}
        </>
    );
};

type ParamProps = {
    params: DataParams;
}

const GameView = ({params}: ParamProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {
        active_languages,
        loaded,
        activeTab,
        is_draft,
        is_published,
        is_archived,
        versions,
        draft_id,
        current_version_id,
        error,
    } = useAppSelector((state) => state.adminGameView);
    const {game_id, version_id} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showConfirmDraft, setShowConfirmDraft] = useState(false);

    const defaultTab = searchParams.get('activeTab');

    useEffect(() => {
        // NOTE: on React.StrictMode, this useEffect is called twice on dev mode
        if (game_id && version_id) {
            dispatch(getAdminGameDetail({game_id, version_id}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game_id, version_id]);

    useEffect(() => {
        if (defaultTab && AVAILABLE_LANGUAGES.includes(defaultTab)) {
            dispatch(changeTab(defaultTab));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultTab]);


    const handleCreateDraft = () => {
        setShowConfirmDraft(true);
    };

    const handleDraftConfirm = () => {
        setShowConfirmDraft(false);
        if (game_id && version_id) {
            dispatch(addDraft({game_id, version_id})).then((resultAction) => {
                if (addDraft.fulfilled.match(resultAction)) {
                    const {game_id: newGameId, current_version_id: newVersionId} = resultAction.payload;
                    navigate(`/admin/games/${newGameId}/versions/${newVersionId}/?activeTab=${activeTab}`);
                }
            });
        }
    };

    const handleMoveToDraft = () => {
        if (draft_id) {
            navigate(`/admin/games/${game_id}/versions/${draft_id}/?activeTab=${activeTab}`);
        }
    }

    const handlePublishPromise = () => {
        if (game_id && current_version_id) {
            dispatch(publishVersionPromise({game_id, version_id: current_version_id}));
        }
    }

    const handleTabSelect = (key: string | null) => {
        if (key) {
            dispatch(changeTab(key));
            setSearchParams('');
            if (defaultTab) {
                searchParams.delete('languageTab');
                window.history.replaceState({}, '', window.location.pathname);
            }
        }
    };

    const handleAddNewSection = () => {
        dispatch(openSectionModal({section_id: null, language: activeTab}));
    }
    const handleVersionSelect = (version_id: string) => {
        navigate(`/admin/games/${game_id}/versions/${version_id}/?activeTab=${activeTab}`);
    }

    const currentVersion = versions.find(version => version.version_id === current_version_id);

    const versionDropdown = (
        <Dropdown>
            <Dropdown.Toggle variant='outline-primary'>
                {getVersionIcon(is_published, is_draft, is_archived)} {ADMIN_LANG.version} #{currentVersion?.number}
                <ChevronDown size='1.5rem' className="ms-2"/>
            </Dropdown.Toggle>
            <Dropdown.Menu>
                {versions
                    .filter(version => version.version_id !== version_id)
                    .map((version) => (
                        <Dropdown.Item
                            key={version.version_id}
                            onClick={() => handleVersionSelect(version.version_id)}
                            className={getVersionDetails(version.is_published, version.is_draft, version.is_archived).className}
                        >
                            {getVersionIcon(version.is_published, version.is_draft, version.is_archived)} {ADMIN_LANG.version} #{version.number}
                        </Dropdown.Item>
                    ))}
            </Dropdown.Menu>
        </Dropdown>
    );

    const tabsConfig = [
        {
            key: "default",
            title: ADMIN_LANG.common_info,
            content: <CommonDetails/>,
            icon: <Grid/>
        },
        {
            key: "sessions",
            title: ADMIN_LANG.sessions,
            content: <AdminGameSessions params={params}/>,
            icon: <BarChart/>
        },
        ...active_languages.map(language => ({
            key: language,
            title: `${ADMIN_LANG.sections_and_content} (${language.toUpperCase()})`,
            content: <SectionDetails language={language} addNewSection={handleAddNewSection}/>,
            icon: <Gear/>  // Gear icon for language sections
        }))
    ];
    const activeTabTitle = tabsConfig.find(tab => tab.key === activeTab)?.title || '';


    if (error) {
        return (
            <ErrorPage message={error}/>
        )
    }


    return (
        <Container>
            <Row className='pb-4 pt-4'>
                <Col xs={12}>
                    {!loaded && <AdminLoader/>}
                    <div className='admin-tabs d-lg-flex justify-content-lg-between mb-4'>
                        <div className='d-flex align-items-center pb-3 pb-lg-0'>
                            <Dropdown onSelect={handleTabSelect}>
                                <Dropdown.Toggle variant="primary" id="tab-dropdown">
                                    {tabsConfig.find(tab => tab.key === activeTab)?.icon} <span
                                    className='ms-1'>{activeTabTitle}</span>
                                    <ChevronDown size='1.5rem' className="ms-2"/>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {tabsConfig.map(tab => (
                                        <Dropdown.Item eventKey={tab.key} key={tab.key} active={tab.key === activeTab}>
                                            {tab.icon} <span className='ms-1'>{tab.title}</span>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                            {/*{preview_url &&
                                <span onClick={handleOpenPreview} className='ms-2 link-span'>
                                <Eye width='2rem' height='2rem' title={ADMIN_LANG.preview}/>
                            </span>
                            }*/}
                        </div>
                        <div className='d-flex align-items-center'>
                            {is_draft &&
                                <Button variant='success' className='me-2'
                                        onClick={handlePublishPromise}>{ADMIN_LANG.publish_version}</Button>
                            }
                            {is_published && !draft_id &&
                                <Button variant='info' onClick={handleCreateDraft}
                                        className='me-2'>{ADMIN_LANG.create_new_version}</Button>
                            }
                            {is_published && draft_id &&
                                <Button variant='info' onClick={handleMoveToDraft}
                                        className='me-2'>{ADMIN_LANG.continue_editing}</Button>
                            }
                            {versionDropdown}
                        </div>
                    </div>
                    <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="d-none">
                        {tabsConfig.map(tab => (
                            <Tab eventKey={tab.key} title={tab.title} key={tab.key}>
                                {tab.content}
                            </Tab>
                        ))}
                    </Tabs>
                </Col>
            </Row>
            <QuestionEditModal is_draft={is_draft}/>
            <SectionEditModal is_draft={is_draft}/>
            <SectionOrderModal/>
            <VersionPublishModal/>
            {FEATURE_PDF_UPLOAD && <SectionPdfModal/>}
            <Confirm
                show={showConfirmDraft}
                title={`${ADMIN_LANG.create} ${ADMIN_LANG.version}`}
                message={ADMIN_LANG.confirm_draft_create}
                onConfirm={handleDraftConfirm}
                onCancel={() => setShowConfirmDraft(false)}
            />
        </Container>
    );
}

export default GameView;


const CommonDetails = () => {
    const dispatch = useAppDispatch();
    const {
        average_duration,
        average_points,
        played,
        sections,
        max_points,
        min_points,
        languages,
        latest_sessions,
        slides,
        questions,
        pages,
        name,
        restrict_playing,
        display_correct_answers,
        teams,
        current_version_id,
        game_id,
        active_languages,
        statistics_url
    } = useAppSelector((state) => state.adminGameView);
    const navigate = useNavigate();

    const handleOpenEditModal = (language: string) => {
        if (game_id && current_version_id && language) {
            dispatch(openModal({game_id, version_id: current_version_id, language}));
        }
    }

    const handleAddNewLanguage = (language: string) => {
        if (game_id && current_version_id) {
            dispatch(addGameLanguage({game_id, version_id: current_version_id, language_code: language}))
        }
    }

    const handleTabSelect = (key: string | null) => {
        if (key) {
            dispatch(changeTab(key));
        }
    };

    const handleOpenPreview = (url?: string) => {
        if (url) {
            dispatch(openPreview(url));
        }
    }


    const handleCopyGame = () => {
        dispatch(duplicateGame({game_id, version_id: current_version_id}))
            .then((response) => {
                if (response.payload) {
                    navigate(`/admin/games/${response.payload.game_id}/versions/${response.payload.draft_id}/`);
                }
            })
    }

    const languageRows = languages.map((lang: GameLanguages) => {
        const publishedUrl = lang.status === STATUS_PUBLISHED && lang.slug ? `/games/${lang.slug}/` : '';
        return (
            <tr key={lang.language_code}>
                <td>
                    {lang.preview_url &&
                        <span onClick={() => handleOpenPreview(lang.preview_url)}><Eye
                            className='link-span me-2'/></span>
                    }
                    {lang.name}</td>
                <td>{lang.is_active ? <a target='_blank' rel="noreferrer" href={publishedUrl}>{lang.slug}</a> :
                    <span>{lang.slug}</span>}</td>
                <td>{lang.language_name}</td>
                <td>{lang.is_active ? <CheckCircleFill className="text-success"/> :
                    <XCircleFill className="text-danger"/>}</td>
                <td className='text-end'>
                    <Button variant='outline-primary btn-sm me-2'
                            onClick={() => handleTabSelect(lang.language_code)}>{ADMIN_LANG.sections_and_content}</Button>
                    <Button variant='primary btn-sm'
                            onClick={() => handleOpenEditModal(lang.language_code)}>{ADMIN_LANG.settings}</Button>
                </td>
            </tr>
        );
    });

    const minPoints = min_points >= 0 && min_points <= 100 ? (min_points / 100) * max_points : 0;
    const filteredLanguages = AVAILABLE_LANGUAGES.filter(lang => !active_languages.includes(lang));

    return (
        <Row className='gy-3'>
            <Col xs={12}>
                <h2 className='mb-0'>{name}</h2>
            </Col>
            <GameCard
                icon={Trophy}
                items={[
                    {label: ADMIN_LANG.sections, value: sections},
                    {label: ADMIN_LANG.questions, value: questions},
                    {label: ADMIN_LANG.slides, value: slides},
                    {label: ADMIN_LANG.pages, value: pages}
                ]}
                buttonConfig={{
                    label: ADMIN_LANG.statistics,
                    onClick: () => window.open(statistics_url, '_blank'),
                    icon: BoxArrowUpRight,
                    variant: 'outline-primary btn-sm mt-2'
                }}
            />

            <GameCard
                icon={Stopwatch}
                items={[
                    {label: ADMIN_LANG.games_played, value: played},
                    {label: ADMIN_LANG.average_points, value: average_points},
                    {label: ADMIN_LANG.max_points, value: max_points},
                    {label: ADMIN_LANG.average_duration, value: formatDuration(average_duration)}
                ]}
            />

            <GameCard
                icon={Gear}
                items={[
                    {label: ADMIN_LANG.only_login, value: restrict_playing},
                    {label: ADMIN_LANG.display_correct_answers, value: display_correct_answers},
                    {label: ADMIN_LANG.game_min_points, value: minPoints.toFixed(0)},
                    {label: ADMIN_LANG.allowed_teams, value: teams.length > 0}
                ]}
            />
            <Col xs={12}>
                <div className='admin-card'>
                    <div className='d-flex align-items-center'>
                        <h3 className='mb-0'>
                            {ADMIN_LANG.language_versions}
                        </h3>
                        {filteredLanguages.length > 0 &&
                            <Dropdown className='language-dropdown ms-2'>
                                <Dropdown.Toggle variant='language'><PlusCircleFill size='1.8rem'
                                                                                    title={ADMIN_LANG.add_language}/></Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {filteredLanguages.map((lang) => (
                                        <Dropdown.Item href="#" key={lang}
                                                       onClick={() => handleAddNewLanguage(lang)}>
                                            <span className='text-uppercase'>{lang}</span>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        }
                    </div>
                    <div className='table-container'>
                        <Table responsive hover>
                            <thead>
                            <tr>
                                <th>{ADMIN_LANG.name}</th>
                                <th>{ADMIN_LANG.slug}</th>
                                <th>{ADMIN_LANG.language}</th>
                                <th>{ADMIN_LANG.used_in}</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {languageRows}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </Col>
            {latest_sessions.length > 0 &&
                <Col xs={12}>
                    <div className='admin-card'>
                        <div className='pb-2'>
                            <h3>{ADMIN_LANG.latest_sessions}</h3>
                        </div>
                        <SessionTable sessions={latest_sessions}/>
                    </div>
                </Col>
            }
            <Col xs={12}>
                <Button variant='outline-primary' onClick={handleCopyGame}>{ADMIN_LANG.duplicate}</Button>
            </Col>
        </Row>
    );
}


/*
<Col xs={12}>
    <Button variant='outline-primary' onClick={handleCopyGame}>{ADMIN_LANG.duplicate}</Button>
</Col>
*/

